import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import MaterialIcon from './MaterialIcon';
import ClothedEyeLoader from './ClothedEyeLoader';
import databaseService from '../services/databaseService';
import logger from '../utils/logger.js';
import './SimpleStickerCard.css';

const SimpleStickerCard = ({
  listingId,
  title = '',
  description = '',
  etsyUrl = '',
  category = '',
  onAdd,
  globalSize = 'MICRO',
  globalColor = 'Black',
  isAdminMode = false,
  onCardClick = null,
  buttonText = 'BUY IT NOW',
  buttonIcon = 'add',
}) => {
  // State for lightweight data
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load lightweight data
  const loadData = useCallback(async () => {
    if (!listingId) {
      setError('No listing ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      logger.log(`🔍 Loading UNIFIED data for listing ${listingId}...`);
      const lightweightData = await databaseService.getEtsyListingData(listingId);

      if (!lightweightData) {
        setError('No data found for this listing');
        setLoading(false);
        return;
      }

      setData(lightweightData);
      setLoading(false);
    } catch (err) {
      console.error('❌ Error loading lightweight data:', err);
      setError('Failed to load listing data');
      setLoading(false);
    }
  }, [listingId]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle image loading
  const handleImageLoad = useCallback(() => {
    setImageLoading(false);
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageLoading(false);
    setImageLoaded(false);
  }, []);

  // Handle add to scene
  const handleAddToScene = useCallback(() => {
    if (!data) return;

    const stickerData = {
      listingId,
      title: data.title,
      size: globalSize,
      color: globalColor,
      image: data.largestSizeImageUrl || data.firstImageUrl,
      etsyUrl: data.etsyUrl,
    };

    if (onAdd) {
      onAdd(stickerData);
    }
  }, [data, listingId, globalSize, globalColor, onAdd]);

  // Handle card click
  const handleCardClick = useCallback(() => {
    if (onCardClick) {
      onCardClick();
    }
  }, [onCardClick]);

  // Loading state
  if (loading) {
    return (
      <div className="simple-sticker-card loading">
        <div className="simple-loading-container">
          <ClothedEyeLoader size="small" showTagline={false} className="simple-card-loader" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="simple-sticker-card error">
        <div className="simple-error-content">
          <MaterialIcon icon="error" size={24} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="simple-sticker-card">
      {/* Square Image Section */}
      <div className="simple-card-image-container">
        {data?.firstImageUrl ? (
          <img
            src={data.firstImageUrl}
            alt={data.title || 'Sticker'}
            className="simple-card-image"
            loading="lazy"
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        ) : null}
        {(!data?.firstImageUrl || !imageLoaded) && (
          <div className="simple-image-placeholder">
            <img
              src="/assets/svgs/clothedEye-watermark.svg"
              alt="ClothedEye"
              className="placeholder-logo"
            />
          </div>
        )}
      </div>

      {/* Minimal Content Section */}
      <div className="simple-card-content">
        {/* Title - First 21 characters only */}
        <h3 className="simple-card-title">
          {(data?.title || 'Sticker').substring(0, 21)}
          {(data?.title || 'Sticker').length > 21 && '...'}
        </h3>
      </div>
    </div>
  );
};

SimpleStickerCard.propTypes = {
  listingId: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  etsyUrl: PropTypes.string,
  category: PropTypes.string,
  onAdd: PropTypes.func,
  globalSize: PropTypes.string,
  globalColor: PropTypes.string,
  isAdminMode: PropTypes.bool,
  onCardClick: PropTypes.func,
  buttonText: PropTypes.string,
  buttonIcon: PropTypes.string,
};

// Memoize to prevent unnecessary re-renders in large grids
// Only re-render if listingId changes (data is immutable per listing)
export default React.memo(SimpleStickerCard, (prevProps, nextProps) => {
  return prevProps.listingId === nextProps.listingId;
});
